import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errText } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Mail, Send, Bell, CalendarClock } from "lucide-react";

const fmtDateTime = (iso) => iso ? new Date(iso).toLocaleString() : "—";

export default function NotificationsPanel() {
  const [integrations, setIntegrations] = useState(null);
  const [digestInfo, setDigestInfo] = useState(null);
  const [brands, setBrands] = useState([]);
  const [sendingDenzel, setSendingDenzel] = useState(false);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [lastResults, setLastResults] = useState({});

  useEffect(() => {
    api.get("/integrations/status")
      .then((r) => setIntegrations(r.data))
      .catch(() => {});
    api.get("/digest/preview")
      .then((r) => setDigestInfo(r.data))
      .catch(() => {});
    api.get("/brands")
      .then((r) => setBrands(r.data || []))
      .catch(() => {});
  }, []);

  const resend = (integrations?.integrations || []).find((i) => i.key === "resend");
  const emailConfigured = resend?.connected && !!digestInfo?.recipient;
  const recipient = digestInfo?.recipient || "";

  const sendDenzelNow = async () => {
    setSendingDenzel(true);
    const w = toast.loading("Sending Denzel share reminder…");
    try {
      const { data } = await api.post("/denzel/share-tray/send-reminder");
      toast.dismiss(w);
      setLastResults((r) => ({ ...r, denzel: data }));
      if (data.sent) toast.success(`Reminder emailed · ${data.count} pending`);
      else toast.message(data.reason === "nothing_stale"
                        ? "Nothing stale — no email sent"
                        : (data.error || data.reason || "Not sent"));
    } catch (e) {
      toast.dismiss(w);
      toast.error(errText(e));
    } finally { setSendingDenzel(false); }
  };

  const sendRecap = async (brand) => {
    setSendingRecap(true);
    const label = brand ? `${brand} recap` : "all brand recaps";
    const w = toast.loading(`Sending ${label}…`);
    try {
      const { data } = await api.post(`/analytics/weekly-recap/send-email${brand ? `?brand=${encodeURIComponent(brand)}` : ""}`);
      toast.dismiss(w);
      setLastResults((r) => ({ ...r, recap: data }));
      if (brand) {
        if (data.sent) toast.success(`${brand}: sent · ${data.recap?.total_posts || 0} posts recap'd`);
        else toast.error(`${brand}: ${data.error || data.reason || "not sent"}`);
      } else {
        const ok = (data.results || []).filter((r) => r.sent).length;
        toast.success(`${ok}/${(data.results || []).length} recap emails sent`);
      }
    } catch (e) {
      toast.dismiss(w);
      toast.error(errText(e));
    } finally { setSendingRecap(false); }
  };

  return (
    <div className="space-y-6" data-testid="notifications-panel">
      <Card data-testid="notif-status-card">
        <CardHeader>
          <CardTitle className="font-heading text-xl flex items-center gap-2"><Mail size={18} /> Email notifications</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">All notification emails go via Resend to your team address. Sunday-evening recaps and Denzel-tray reminders run automatically — the buttons below let you fire them right now for a test.</p>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Resend</div>
              <div className="mt-1"><Badge variant={emailConfigured ? "default" : "destructive"}>{emailConfigured ? "Connected" : "Not configured"}</Badge></div>
              {resend?.notes?.length ? <p className="text-xs text-muted-foreground mt-2">{resend.notes.join(" · ")}</p> : null}
            </div>
            <div className="rounded-lg border p-3">
              <div className="text-xs text-muted-foreground">Recipient</div>
              <div className="text-sm mt-1 font-mono break-all" data-testid="notif-recipient">{recipient || <span className="text-destructive">DIGEST_RECIPIENT not set</span>}</div>
              <p className="text-[11px] text-muted-foreground mt-2">Configured via <code>DIGEST_RECIPIENT</code> env var.</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="notif-denzel-card">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2"><Bell size={16} /> Denzel Share reminder</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Sends a nudge email if any post has been sitting in the Denzel Share Tray unshared for more than 24 hours. Runs once per day automatically.</p>
          </div>
          <Button size="sm" onClick={sendDenzelNow} disabled={sendingDenzel || !emailConfigured}
                  data-testid="notif-send-denzel-btn">
            <Send size={14} className="mr-2" />
            {sendingDenzel ? "Sending…" : "Send now"}
          </Button>
        </CardHeader>
        {lastResults.denzel && (
          <CardContent>
            <div className="rounded-lg border p-3 text-xs bg-muted/40">
              <div className="font-semibold mb-1">Last run</div>
              {lastResults.denzel.sent
                ? <div>✓ Emailed {lastResults.denzel.count} pending post{lastResults.denzel.count !== 1 ? "s" : ""} · id <code className="text-[10px]">{lastResults.denzel.email_id}</code></div>
                : <div className="text-muted-foreground">Not sent — {lastResults.denzel.reason || lastResults.denzel.error}</div>}
            </div>
          </CardContent>
        )}
      </Card>

      <Card data-testid="notif-recap-card">
        <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0">
          <div>
            <CardTitle className="font-heading text-lg flex items-center gap-2"><CalendarClock size={16} /> Sunday-evening brand recap</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Every Sunday from 18:00 UTC, each brand's weekly performance (posts, engagement, top pillar, top companion, AI summary) is emailed as a standalone recap. Guarded per-brand per-ISO-week so it can't double-fire.</p>
          </div>
          <Button size="sm" onClick={() => sendRecap()} disabled={sendingRecap || !emailConfigured}
                  data-testid="notif-send-recap-all-btn">
            <Send size={14} className="mr-2" />
            {sendingRecap ? "Sending…" : "Send all now"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2">
          <div className="grid gap-2 sm:grid-cols-2">
            {brands.map((b) => (
              <div key={b.id} className="flex items-center justify-between rounded-lg border p-2 text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="inline-block w-2.5 h-2.5 rounded-full" style={{ background: b.color || "#c69d56" }} />
                  <span className="truncate">{b.name}</span>
                  {b.last_recap_email_at && <Badge variant="outline" className="text-[10px] shrink-0">Last: {fmtDateTime(b.last_recap_email_at)}</Badge>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => sendRecap(b.name)} disabled={sendingRecap || !emailConfigured}
                        data-testid={`notif-send-recap-${b.id}`}>
                  <Send size={12} className="mr-1" /> Send
                </Button>
              </div>
            ))}
          </div>
          {lastResults.recap && (
            <div className="rounded-lg border p-3 text-xs bg-muted/40 mt-3">
              <div className="font-semibold mb-1">Last run</div>
              {Array.isArray(lastResults.recap.results) ? (
                <div className="space-y-1">
                  {lastResults.recap.results.map((r) => (
                    <div key={r.brand} className="flex justify-between">
                      <span>{r.brand}</span>
                      <span className={r.sent ? "text-accent" : "text-destructive"}>{r.sent ? `✓ sent (${r.recap?.total_posts || 0} posts)` : `✗ ${r.error || r.reason}`}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div>{lastResults.recap.sent ? `✓ sent (${lastResults.recap.recap?.total_posts || 0} posts) · id ${lastResults.recap.email_id}` : `✗ ${lastResults.recap.error || lastResults.recap.reason}`}</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
